import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, ListBullets, GridFour, CheckSquare, X, Database } from '@phosphor-icons/react';

const SEARCH_DEBOUNCE_MS = 400; // open-questions.md (726), M2 = A
// Tope del backend para q en GET /api/file-explorer/search (400 si se pasa).
const SEARCH_MAX_LENGTH = 100;

const SORT_OPTIONS = [
  { value: 'size:desc', label: 'Tamaño: mayor a menor', sortBy: 'size', sortOrder: 'desc' },
  { value: 'size:asc', label: 'Tamaño: menor a mayor', sortBy: 'size', sortOrder: 'asc' },
  { value: 'date:desc', label: 'Fecha: más reciente primero', sortBy: 'date', sortOrder: 'desc' },
  { value: 'date:asc', label: 'Fecha: más antigua primero', sortBy: 'date', sortOrder: 'asc' }
];

const viewToggleButtonClasses = (isActive) =>
  `inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${
    isActive
      ? 'bg-amber border-amber text-on-amber'
      : 'bg-surface border-line text-content-muted hover:text-content hover:border-line'
  }`;

/**
 * Búsqueda global (toda la company, no la carpeta actual).
 * initialSearch: término con el que arranca el input (al volver al explorador
 * desde el detalle de una tarea abierta desde una búsqueda).
 * resetKey: al cambiar, limpia el término local — p. ej. al abrir la carpeta
 * de un resultado, para volver de la vista de resultados al explorador.
 */
const FileExplorerToolbar = ({
  initialSearch = '',
  sortBy,
  sortOrder,
  resetKey,
  viewMode,
  onSortChange,
  onSearchChange,
  onViewModeChange,
  canSelect = false,
  selectionMode = false,
  onToggleSelectionMode,
  onOpenMaintenance
}) => {
  const [inputValue, setInputValue] = useState(initialSearch);
  const debounceRef = useRef(null);
  const isFirstRender = useRef(true);

  const lastResetKey = useRef(resetKey);

  // Solo ante un cambio real de resetKey: al montar no se limpia, para no
  // pisar initialSearch.
  useEffect(() => {
    if (lastResetKey.current === resetKey) {
      return;
    }

    lastResetKey.current = resetKey;
    setInputValue('');
  }, [resetKey]);

  useEffect(() => {
    // Evita disparar un fetch redundante al montar (FileExplorerScreen ya hace el fetch inicial)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearchChange(inputValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const currentSortValue = `${sortBy}:${sortOrder}`;

  const handleSortSelect = (event) => {
    const option = SORT_OPTIONS.find((opt) => opt.value === event.target.value);
    if (option) {
      onSortChange(option.sortBy, option.sortOrder);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-b border-line">
      <div className="flex items-center gap-1.5 bg-surface-2 border border-line rounded-md px-3 py-1.5 min-w-0 focus-within:ring-2 focus-within:ring-amber">
        <MagnifyingGlass size={15} className="text-content-muted flex-shrink-0" />
        <input
          type="text"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          maxLength={SEARCH_MAX_LENGTH}
          placeholder="Buscar archivos..."
          aria-label="Buscar archivos en todas las tareas"
          className="bg-transparent text-sm text-content placeholder-content-muted outline-none w-40 min-w-0"
        />
      </div>

      <select
        value={currentSortValue}
        onChange={handleSortSelect}
        className="bg-surface-2 border border-line text-content text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber"
      >
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <div className="flex items-center gap-2">
        <button
          type="button"
          title="Vista de lista"
          onClick={() => onViewModeChange('list')}
          className={viewToggleButtonClasses(viewMode === 'list')}
        >
          <ListBullets size={16} />
        </button>
        <button
          type="button"
          title="Vista de grilla"
          onClick={() => onViewModeChange('grid')}
          className={viewToggleButtonClasses(viewMode === 'grid')}
        >
          <GridFour size={16} />
        </button>
      </div>

      {canSelect && (
        <>
          <div className="w-px h-6 bg-line" aria-hidden="true" />
          <button
            type="button"
            onClick={onToggleSelectionMode}
            aria-pressed={selectionMode}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              selectionMode
                ? 'bg-amber border-amber text-on-amber hover:bg-amber-hover'
                : 'bg-surface border-line text-content-muted hover:text-content hover:border-line'
            }`}
          >
            {selectionMode ? <X size={16} /> : <CheckSquare size={16} />}
            {selectionMode ? 'Cancelar selección' : 'Seleccionar'}
          </button>

          <button
            type="button"
            onClick={onOpenMaintenance}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium bg-surface border-line text-content-muted transition-colors hover:text-content hover:border-line"
          >
            <Database size={16} />
            Mantenimiento
          </button>
        </>
      )}
    </div>
  );
};

export default FileExplorerToolbar;
